import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  resolveTtqWorkbenchModelDisplay,
  TTQ_WORKBENCH_MODEL_OPTIONS,
  type TtqWorkbenchModelOption,
  writeStoredTtqWorkbenchModelId,
} from './ttqWorkbenchModels';

type TtqWorkbenchModelMenuProps = {
  value?: string;
  disabled?: boolean;
  /** 变化时回传完整 option，便于写入请求体 */
  onChange?: (option: TtqWorkbenchModelOption) => void;
};

/** 品题底栏 · 发送旁模型选择（默认读 localStorage） */
export function TtqWorkbenchModelMenu({
  value,
  disabled = false,
  onChange,
}: TtqWorkbenchModelMenuProps) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(() => value || readStoredTtqWorkbenchModelId());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) setSelectedId(getTtqWorkbenchModelOption(value).id);
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selected = getTtqWorkbenchModelOption(selectedId);
  const display = resolveTtqWorkbenchModelDisplay(selected);

  const selectOption = (option: TtqWorkbenchModelOption) => {
    if (!option.selectable || disabled) return;
    setSelectedId(option.id);
    writeStoredTtqWorkbenchModelId(option.id);
    onChange?.(option);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`大模型：${display.label}`}
        onClick={() => setOpen((prev) => !prev)}
        className={
          'inline-flex max-w-[9.5rem] items-center gap-1 rounded-lg border border-border bg-canvas/70 px-2 py-1.5 text-[11px] font-medium text-ink '
          + (disabled
            ? 'cursor-not-allowed opacity-60'
            : 'hover:bg-black/[0.04]')
        }
      >
        {display.iconUrl ? (
          <img src={display.iconUrl} alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm" />
        ) : (
          <span className="h-3.5 w-3.5 shrink-0 rounded-sm bg-black/10" />
        )}
        <span className="truncate">{display.label}</span>
        <ChevronDown size={12} className="shrink-0 text-muted" />
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute bottom-full right-0 z-50 mb-1.5 w-60 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
        >
          <p className="border-b border-border px-2.5 py-1.5 text-[10px] font-medium text-muted">
            选择大模型
          </p>
          <ul className="max-h-72 overflow-y-auto py-1">
            {TTQ_WORKBENCH_MODEL_OPTIONS.map((option) => {
              const itemDisplay = resolveTtqWorkbenchModelDisplay(option);
              const isActive = option.id === selected.id;
              return (
                <li key={option.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    disabled={!option.selectable}
                    onClick={() => selectOption(option)}
                    className={
                      'flex w-full items-start gap-2 px-2.5 py-2 text-left text-[11px] '
                      + (!option.selectable
                        ? 'cursor-not-allowed opacity-50'
                        : isActive
                          ? 'bg-primary/10 text-ink'
                          : 'text-ink hover:bg-black/[0.04]')
                    }
                  >
                    {itemDisplay.iconUrl ? (
                      <img src={itemDisplay.iconUrl} alt="" className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm" />
                    ) : (
                      <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm bg-black/10" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="truncate">{option.label}</span>
                        {option.recommended ? (
                          <span className="shrink-0 rounded bg-primary/15 px-1 py-px text-[9px] text-primary">默认</span>
                        ) : null}
                      </span>
                      {option.subtitle ? (
                        <span className="mt-0.5 block text-[10px] text-muted">{option.subtitle}</span>
                      ) : null}
                    </span>
                    {isActive ? <Check size={12} className="mt-0.5 shrink-0 text-primary" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

type TtqWorkbenchModelIconProps = {
  modelId?: string;
  titleSuffix?: string;
};

/** 预览等场景：仅展示当前模型图标，不可切换 */
export function TtqWorkbenchModelIconLocked({
  modelId,
  titleSuffix = '预览暂不可换模型',
}: TtqWorkbenchModelIconProps) {
  const option = getTtqWorkbenchModelOption(modelId || readStoredTtqWorkbenchModelId());
  const display = resolveTtqWorkbenchModelDisplay(option);
  return (
    <span
      className="inline-flex h-7 w-7 shrink-0 cursor-not-allowed items-center justify-center rounded-md border border-border bg-canvas/50 opacity-80"
      title={`${display.label} · ${titleSuffix}`}
      aria-label={`${display.label}，${titleSuffix}`}
    >
      {display.iconUrl ? (
        <img src={display.iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
      ) : (
        <span className="h-3.5 w-3.5 rounded-sm bg-black/10" />
      )}
    </span>
  );
}
